import { useAuth0 } from '@auth0/auth0-react';
import { useAtomValue } from 'jotai';

import { useMomentumPilotPatientAccessQuery } from '../../hooks/intentions/useMomentumPilotPatientAccessQuery';
import { sessionInfoAtom } from '../../state';

// Momentum pilot feature key for Grow-initiated (real-time) session recaps.
// Must match the monolith's `momentumPilotFeatureForPatient` resolver.
export const REAL_TIME_AVS_KEY = 'real_time_avs';

/**
 * Which variant of the end-of-session nudge banner to show:
 * - `legacy`: the pre-existing "Grow Tip / Leave Session" banner. Shown to
 *   patients who are NOT in the Momentum session-recap pilot.
 * - `recap`: the Momentum recap banner, for pilot patients who have session
 *   recaps enabled (haven't opted out of `SEND_SESSION_RECAP`).
 * - `noRecap`: the Momentum "we'll guide you through next steps" banner, for
 *   pilot patients who have opted out of session recaps.
 */
export type SessionRecapBannerVariant = 'legacy' | 'recap' | 'noRecap';

/**
 * Determines which end-of-session nudge banner variant a patient should see.
 *
 * Pilot membership is a monolith check (`momentumPilotFeatureForPatient`); the
 * opt-out is read from `telehealthSessionInfo.sessionRecapEnabled`, which is
 * populated into `sessionInfoAtom` by `useEndOfSessionToast`'s session-info
 * query. Absence of a preference record is an implied opt-in.
 *
 * @param patientShortId - The patient's short ID (from the meeting-room route).
 */
export function usePatientSessionRecapAccess(patientShortId: string | undefined, { skip }: { skip?: boolean } = {}) {
  // The pilot check is an authenticated monolith query — skip it for guest
  // visitors, who can't be identified for pilot enrollment and would otherwise
  // trip the missing-auth-header guard in the Apollo auth link.
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const shouldSkip = Boolean(!patientShortId || isAuthLoading || !isAuthenticated || skip);

  const { pilotAccess, loading } = useMomentumPilotPatientAccessQuery({
    patientShortId: patientShortId ?? '',
    key: REAL_TIME_AVS_KEY,
    // Web clients never send the `graphql-client-app-version` header, so the
    // app-version gate must be skipped (see MOMENT-420).
    shouldSkipAppVersionCheck: true,
    skip: shouldSkip,
  });

  const sessionInfo = useAtomValue(sessionInfoAtom);
  // Default to enabled (implied opt-in) until session info resolves.
  const isRecapEnabled = sessionInfo?.sessionRecapEnabled ?? true;

  let variant: SessionRecapBannerVariant = 'legacy';
  if (!shouldSkip && pilotAccess?.enabled) {
    variant = isRecapEnabled ? 'recap' : 'noRecap';
  }

  return {
    variant,
    isLoading: Boolean(isAuthLoading || loading),
  };
}
